'use client';
import useAutosaveContext from '@susu/custom-made/providers/hooks/useAutosaveContext';
import useGlobalContext from '@susu/custom-made/providers/hooks/useGlobalContext';
import useMissingSelectionValuesContext from '@susu/custom-made/providers/hooks/useMissingSelectionValuesContext';
import type { OptionConfigurationType, typeOptionValue } from '@susu/custom-made/utils/interfaces';
import { SECTION_CONTAINER_TEST_IDS } from '@susu/custom-made/utils/testIds';
import { useI18N } from '@susu/i18n/client';
import { memo } from 'react';

import { ButtonType } from '../button/button';
import MissingOptionsDetails from '../missingOptionDetails/missingOptionsDetails';
import Modal from '../modal/modal';
import Panel, { PanelSize } from '../panel/panel';
import ToastMessage from '../toastMessage/toastMessage';

export const MissingOptionsOverlay = memo(function MissingOptionsOverlay({
  options,
  optionValues,
}: Readonly<{
  options: OptionConfigurationType;
  optionValues: typeOptionValue;
}>) {
  const {
    showMissingOptionsPanel,
    setShowMissingOptionsPanel,
    missingOptionValuesPerConfig,
    missingMonogramValuesPerConfig,
    missingOptionsPerConfig,
    isSummaryValidationTriggered,
  } = useMissingSelectionValuesContext();

  return (
    <Panel
      isActive={showMissingOptionsPanel}
      closeClick={() => setShowMissingOptionsPanel(false)}
      panelSize={PanelSize.Content}
      hasCloseBtn={true}
      withOverlay={true}
      testIdSubfix="missing-options"
    >
      <MissingOptionsDetails
        options={options}
        optionValues={optionValues}
        missingOptionValuesPerConfig={missingOptionValuesPerConfig}
        missingMonogramValuesPerConfig={missingMonogramValuesPerConfig}
        missingOptionsPerConfig={missingOptionsPerConfig}
        isSummaryValidationFlow={isSummaryValidationTriggered}
      />
    </Panel>
  );
});

export const ConfiguratorModals = memo(function ConfiguratorModals() {
  const { t } = useI18N();
  const { showMissingFabricModal, setShowMissingFabricModal } = useMissingSelectionValuesContext();
  const { isAutoSaveModalOpen, closeAutoSaveModal, resumeAutoSave } = useAutosaveContext();

  if (showMissingFabricModal) {
    return (
      <div>
        <Modal
          isOpen={true}
          title={t('sectionContainer.oosModal.title')}
          description={t('sectionContainer.oosModal.description')}
          firstButtonText={t('sectionContainer.oosModal.changeFabric')}
          firstButtonType={ButtonType.Primary}
          isFullSize={true}
          onFirstButtonClick={() => setShowMissingFabricModal(false)}
          testId={SECTION_CONTAINER_TEST_IDS.OOS_FABRIC_MODAL}
        />
      </div>
    );
  }

  return (
    <div>
      <Modal
        isOpen={isAutoSaveModalOpen}
        title={t('sectionContainer.autosaveModal.title')}
        description={t('sectionContainer.autosaveModal.description')}
        firstButtonText={t('sectionContainer.autosaveModal.resume')}
        firstButtonType={ButtonType.Primary}
        secondButtonText={t('sectionContainer.autosaveModal.startFresh')}
        secondButtonType={ButtonType.Tertiary}
        onFirstButtonClick={resumeAutoSave}
        onSecondButtonClick={closeAutoSaveModal}
        isFullSize={true}
        testId={SECTION_CONTAINER_TEST_IDS.RESUME_AUTOSAVE_MODAL}
      />
    </div>
  );
});

export const ConfiguratorToast = memo(function ConfiguratorToast() {
  const { toast, hideToast } = useGlobalContext();

  return <ToastMessage message={toast.message} isOpen={toast.isOpen} onClose={hideToast} />;
});
